// Heap-tree helpers ---------------------------------------------------------
const sireOf = (i) => 2 * i + 1;
const damOf = (i) => 2 * i + 2;

// Build a "name DAG" from the heap-indexed ancestors map. Each unique name
// has a primary heap position (the smallest index where it appears) and
// inherits its sire/dam from that primary position's children in the tree.
const buildNameDag = (ancestors) => {
  const primaryIdx = {}; // name -> smallest heap idx
  const nameAt = {}; // heap idx -> name
  for (const [idxStr, a] of Object.entries(ancestors || {})) {
    if (!a || !a.name || !a.name.trim()) continue;
    const idx = parseInt(idxStr, 10);
    const name = a.name.trim().toLowerCase();
    nameAt[idx] = name;
    if (primaryIdx[name] === undefined || idx < primaryIdx[name]) {
      primaryIdx[name] = idx;
    }
  }
  const parents = {}; // name -> { sire, dam } (names or "" if unknown)
  for (const name of Object.keys(primaryIdx)) {
    const idx = primaryIdx[name];
    parents[name] = {
      sire: nameAt[sireOf(idx)] || "",
      dam: nameAt[damOf(idx)] || "",
    };
  }
  const subjSire = nameAt[1] || "";
  const subjDam = nameAt[2] || "";
  // Depth of each name's primary position from the SUBJECT (root). Used as a
  // proxy for "generation index" — older individuals have larger depth.
  const depthOf = {};
  for (const [name, idx] of Object.entries(primaryIdx)) {
    depthOf[name] = Math.floor(Math.log2(idx + 1));
  }
  return { parents, subjSire, subjDam, depthOf, primaryIdx, nameAt };
};

const displayName = (rawName, ancestors, primaryIdx) =>
  ancestors?.[primaryIdx[rawName]]?.name || rawName;

/**
 * Wright's coefficient of inbreeding via the tabular / recursive method.
 *
 *   F(X)        = 0.5 × a(sire(X), dam(X))
 *   a(X, X)     = 1 + F(X)
 *   a(X, Y)     = 0.5 × ( a(sire(X), Y) + a(dam(X), Y) )      when X is younger
 *                = 0.5 × ( a(X, sire(Y)) + a(X, dam(Y)) )      when Y is younger
 *
 * Crucially, this method does NOT double-count common ancestors that
 * themselves descend from other common ancestors (the "F_A propagation"
 * is handled implicitly via a(X, X) = 1 + F(X)).
 */
export const computeCOI = (ancestors) => {
  if (!ancestors) return { coi: 0, contributors: [], recursive: true };
  const dag = buildNameDag(ancestors);
  const { parents, subjSire, subjDam, depthOf, primaryIdx } = dag;

  const fMemo = new Map();
  const aMemo = new Map();
  const aInProgress = new Set();
  const fInProgress = new Set();

  const F = (x) => {
    if (!x) return 0;
    if (fMemo.has(x)) return fMemo.get(x);
    if (fInProgress.has(x)) return 0;
    fInProgress.add(x);
    const p = parents[x];
    let val = 0;
    if (p && (p.sire || p.dam)) {
      val = 0.5 * additive(p.sire, p.dam);
    }
    fInProgress.delete(x);
    fMemo.set(x, val);
    return val;
  };

  function additive(x, y) {
    if (!x || !y) return 0;
    const key = x < y ? `${x}|${y}` : `${y}|${x}`;
    if (aMemo.has(key)) return aMemo.get(key);
    if (aInProgress.has(key)) return 0;
    aInProgress.add(key);

    let val;
    if (x === y) {
      val = 1 + F(x);
    } else {
      const dx = depthOf[x] ?? 0;
      const dy = depthOf[y] ?? 0;
      // Recurse on the YOUNGER individual (smaller depth from subject).
      const px = parents[x];
      const py = parents[y];
      if (dx <= dy && px && (px.sire || px.dam)) {
        val = 0.5 * (additive(px.sire, y) + additive(px.dam, y));
      } else if (py && (py.sire || py.dam)) {
        val = 0.5 * (additive(x, py.sire) + additive(x, py.dam));
      } else if (px && (px.sire || px.dam)) {
        val = 0.5 * (additive(px.sire, y) + additive(px.dam, y));
      } else {
        val = 0;
      }
    }

    aInProgress.delete(key);
    aMemo.set(key, val);
    return val;
  }

  const coi = subjSire && subjDam ? 0.5 * additive(subjSire, subjDam) : 0;

  // Build contributor table: common ancestors (by name) of sire and dam.
  const collect = (name, set = new Set()) => {
    if (!name || set.has(name)) return set;
    set.add(name);
    const p = parents[name];
    if (p) {
      collect(p.sire, set);
      collect(p.dam, set);
    }
    return set;
  };
  const sireAncs = collect(subjSire);
  const damAncs = collect(subjDam);
  const sireOccCount = {};
  const damOccCount = {};
  for (const [idxStr, a] of Object.entries(ancestors || {})) {
    if (!a?.name?.trim()) continue;
    const idx = parseInt(idxStr, 10);
    if (idx === 0) continue;
    const n = a.name.trim().toLowerCase();
    // Walk up to find subtree root (1 or 2)
    let cur = idx;
    while (cur > 2) cur = Math.floor((cur - 1) / 2);
    if (cur === 1) sireOccCount[n] = (sireOccCount[n] || 0) + 1;
    else damOccCount[n] = (damOccCount[n] || 0) + 1;
  }

  const contributors = [];
  for (const name of sireAncs) {
    if (!damAncs.has(name)) continue;
    contributors.push({
      name: displayName(name, ancestors, primaryIdx),
      sireOccurrences: sireOccCount[name] || 0,
      damOccurrences: damOccCount[name] || 0,
      fA: F(name),
      // Per-contributor contribution is non-trivial to decompose under the
      // tabular method; approximate as 0.5 × a(sire-line, dam-line) collapsed.
      contribution: 0,
    });
  }

  // Approximate each contributor's share by proportional weighting of
  // (1 + F_A) × (0.5 ^ pathlen) over the simplest sire/dam paths.
  const totalWeight =
    contributors.reduce((s, c) => s + (1 + c.fA), 0) || 1;
  contributors.forEach((c) => {
    c.contribution = coi * ((1 + c.fA) / totalWeight);
  });
  contributors.sort((a, b) => b.contribution - a.contribution);

  return { coi, contributors, recursive: true };
};

/**
 * Per-generation × per-part heatmap. Subject (gen 0) included.
 */
export const buildGenerationHeatmap = (ancestors, totalGenerations) => {
  const PARTS = ["ears", "head", "neck", "front", "back", "rear", "tail"];
  const QUALITIES = ["correct", "improve", "faulty", "serious", "unknown"];

  const rows = [];
  for (let g = 0; g < totalGenerations; g++) {
    const counts = {};
    PARTS.forEach((p) => {
      counts[p] = QUALITIES.reduce((acc, q) => ((acc[q] = 0), acc), {});
    });

    const startIdx = Math.pow(2, g) - 1;
    const endIdx = Math.pow(2, g + 1) - 1;
    let filled = 0;
    for (let i = startIdx; i < endIdx; i++) {
      const a = ancestors[String(i)] || ancestors[i];
      if (!a) {
        PARTS.forEach((p) => (counts[p].unknown += 1));
        continue;
      }
      filled += 1;
      PARTS.forEach((p) => {
        const q = (a.parts && a.parts[p]) || "unknown";
        counts[p][q] = (counts[p][q] || 0) + 1;
      });
    }

    const dominant = {};
    PARTS.forEach((p) => {
      const c = counts[p];
      const ranked = ["correct", "improve", "faulty", "serious"]
        .map((q) => ({ q, n: c[q] }))
        .filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n);
      if (ranked.length === 0) dominant[p] = "unknown";
      else if (ranked.length > 1 && ranked[0].n === ranked[1].n)
        dominant[p] = "mixed";
      else dominant[p] = ranked[0].q;
    });

    rows.push({
      gen: g,
      label: g === 0 ? "Subject" : `Gen ${g}`,
      slots: endIdx - startIdx,
      filled,
      counts,
      dominant,
    });
  }
  return rows;
};

/**
 * Build a projected offspring `ancestors` map from a chosen sire pedigree
 * and dam pedigree. The offspring's depth equals min of source pedigrees + 1,
 * capped at 6.
 */
export const buildProjectedOffspring = (sirePed, damPed) => {
  if (!sirePed || !damPed) return { generations: 4, ancestors: {} };
  const generations = Math.min(
    6,
    Math.min(sirePed.generations, damPed.generations) + 1,
  );
  const total = Math.pow(2, generations) - 1;
  const ancestors = {};

  // Subject (index 0) is intentionally empty.
  for (let i = 1; i < total; i++) {
    // Determine which subtree (sire root=1, dam root=2)
    let cur = i;
    while (cur > 2) cur = Math.floor((cur - 1) / 2);
    const inSire = cur === 1;
    const sourcePed = inSire ? sirePed : damPed;

    // Path from subtree root (1 or 2) down to i in OFFSPRING's tree
    // becomes the same path from source's subject (idx 0) down to a source idx
    const bits = [];
    let c = i;
    while (c > 1) {
      bits.unshift(c % 2 === 1 ? 0 : 1); // 0 = sire-step, 1 = dam-step
      c = Math.floor((c - 1) / 2);
    }
    let srcIdx = 0;
    for (const bit of bits) srcIdx = 2 * srcIdx + 1 + bit;
    const src =
      sourcePed.ancestors?.[String(srcIdx)] || sourcePed.ancestors?.[srcIdx];
    if (src) ancestors[String(i)] = JSON.parse(JSON.stringify(src));
  }

  return { generations, ancestors };
};
